<template>
    <div>
        <app-template>
            <div class="space-y-6">

                <!-- Header -->
                <div class="md:flex items-center justify-between">
                    <h1 class="text-2xl font-bold text-gray-800 mb-4 md:mb-0">
                        Gestion des bouteilles de gaz
                    </h1>

                    <div class="flex items-center gap-2">
                        <button v-if="$account.account.societe.abonnement.bouteilles_export" class="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded flex items-center" @click="openExportModal()">
                            <i class="fas fa-file-csv mr-2"></i>
                            Exporter CSV
                        </button>
                        <button
                            @click="openCreate"
                            class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded flex items-center"
                        >
                            <i class="fas fa-plus mr-2"></i>
                            Nouvelle bouteille
                        </button>
                    </div>
                </div>

                <!-- Filtres -->
                <div class="bg-white border rounded-lg p-4 shadow-sm">
                    <div class="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4">

                        <input
                            v-model="filters.numero"
                            placeholder="Numéro"
                            class="border rounded px-3 py-2 focus:outline-none"
                        />

                        <div class="relative group">
                            <i class="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-300"></i>
                            <input
                                v-model="searchFilter"
                                @input="filterFluidesFilter"
                                @focus="showDropdownFilters = true"
                                type="text"
                                id="inp-type-gaz-filter"
                                placeholder="Type de fluide (ex: R32...)"
                                class="pl-10 pr-4 py-2.5 rounded w-full focus:outline-none border border-gray-200"
                            />
                            <ul v-if="showDropdownFilters && filteredFluidesFilter.length" class="absolute z-50 left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 max-h-60 overflow-y-auto py-2">
                                <li
                                    v-for="fluide in filteredFluidesFilter"
                                    :key="fluide.code"
                                    @mousedown.prevent="selectFluideFilter(fluide)"
                                    class="px-5 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                                >
                                    <div class="flex justify-between items-center">
                                        <span class="font-black text-gray-900">{{ fluide.code }}</span>
                                        <span class="text-[10px] font-bold px-2 py-0.5 bg-gray-100 rounded-full text-gray-600">{{ fluide.type }}</span>
                                    </div>
                                    <div class="text-xs text-gray-400 mt-1">{{ fluide.nom }}</div>
                                </li>
                            </ul>
                        </div>

                        <select v-model="filters.etat" class="border rounded px-3 py-2 focus:outline-none">
                            <option :value="null">Tous types</option>
                            <option value="charge">Charge</option>
                            <option value="récupération">Récupération</option>
                        </select>

                        <select v-model="filters.emplacement_id" class="border rounded px-3 py-2 focus:outline-none">
                            <option :value="null">Tous emplacements</option>
                            <option v-for="e in emplacements" :key="e.id" :value="e.id">
                                {{ e.name }}
                            </option>
                        </select>

                        <select v-model="filters.is_recycle" class="border rounded px-3 py-2 focus:outline-none">
                            <option :value="3">Toutes les bouteilles</option>
                            <option :value="1">Bouteilles actives</option>
                            <option :value="2">Bouteilles recyclées</option>
                        </select>

                    </div>
                </div>

                <!-- No results found -->
                <div v-if="!loadingList && bouteilles && bouteilles.length <= 0" class="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-12 text-center shadow-lg">
                    <div class="flex flex-col items-center">
                        <div class="bg-blue-100 rounded-full p-6">
                            <i class="fas fa-inbox text-5xl text-blue-600"></i>
                        </div>
                        <h3 class="text-2xl font-bold text-gray-800 mt-4">Aucune bouteille trouvée</h3>
                        <p class="text-gray-600 max-w-md mt-1">
                            Essayez de modifier les filtres ou créez une nouvelle bouteille.
                        </p>
                    </div>
                </div>

                <!-- Liste -->
                <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                    <!-- Skeleton loading -->
                    <template v-if="loadingList">
                        <div v-for="i in 3" :key="'skeleton-' + i" class="bg-white border rounded-lg p-4 shadow-sm">
                            <div class="flex justify-between items-start">
                                <div class="space-y-2">
                                    <div class="h-4 w-32 bg-gray-200 rounded"></div>
                                    <div class="h-3 w-40 bg-gray-100 rounded"></div>
                                </div>
                                <div class="h-5 w-5 bg-gray-200 rounded"></div>
                            </div>
                            <div class="flex gap-4 mt-4">
                                <div class="w-[85px] flex flex-col items-center">
                                    <div class="h-[140px] w-[70px] bg-gray-200 rounded-xl"></div>
                                    <div class="h-4 w-10 bg-gray-200 rounded mt-3"></div>
                                    <div class="h-3 w-16 bg-gray-100 rounded mt-1"></div>
                                </div>
                                <div class="flex-1 space-y-3 mt-2">
                                    <div class="h-3 w-40 bg-gray-200 rounded"></div>
                                    <div class="flex flex-wrap gap-2 mt-4">
                                        <div class="h-6 w-20 bg-gray-100 rounded"></div>
                                        <div class="h-6 w-24 bg-gray-100 rounded"></div>
                                        <div class="h-6 w-24 bg-gray-100 rounded"></div>
                                        <div class="h-6 w-20 bg-gray-100 rounded"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                    <template v-if="!loadingList">
                        <div v-for="bouteille in bouteilles" :key="bouteille.id" class="bg-white border rounded-lg p-4 shadow-sm">
                            <div class="flex justify-between items-start">
                                <div>
                                    <p class="font-bold text-gray-800">{{ bouteille.numero }}</p>
                                    <p class="text-sm text-gray-500">
                                        {{ bouteille.type_gaz }} · {{ bouteille.etat === 'charge' ? 'Bouteille de charge' : 'Bouteille de récupération' }}
                                    </p>
                                </div>
                                <button @click="openEdit(bouteille)" class="text-gray-400 hover:text-blue-600">
                                    <i class="fas fa-edit"></i>
                                </button>
                            </div>

                            <div class="flex gap-4">
                                <div class="mt-4">
                                    <div class="relative w-[85px]">
                                        <svg viewBox="0 0 100 125" class="w-full h-full" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMinYMid meet">
                                            <defs>
                                                <mask id="bodyMask">
                                                    <path d="M25,45 C25,35 35,30 50,30 C65,30 75,35 75,45 L75,110 C75,118 68,125 60,125 L40,125 C32,125 25,118 25,110 Z" fill="white" />
                                                </mask>
                                            </defs>
                                            <path d="M35,30 L35,15 C35,10 40,5 50,5 C60,5 65,10 65,15 L65,30" stroke="#E5E7EB" stroke-width="8" fill="none" />
                                            <path d="M25,45 C25,35 35,30 50,30 C65,30 75,35 75,45 L75,110 C75,118 68,125 60,125 L40,125 C32,125 25,118 25,110 Z" fill="#E5E7EB" />
                                            <g mask="url(#bodyMask)">
                                                <rect
                                                    x="0"
                                                    :y="125 - (bouteille.pourcentage_remplissage * 0.95)"
                                                    width="100"
                                                    height="100"
                                                    :class="bouteille.etat == 'récupération' ? 'fill-yellow-500' : (bouteille.pourcentage_remplissage < 20 ? 'fill-red-500' : 'fill-green-500')"
                                                    style="transition: y 0.6s cubic-bezier(0.4, 0, 0.2, 1), fill 0.5s;"
                                                />
                                            </g>
                                            <path d="M35,30 L35,15 C35,10 40,5 50,5 C60,5 65,10 65,15 L65,30" stroke="black" stroke-width="5" fill="none" stroke-linecap="round" />
                                            <path d="M25,45 C25,35 35,30 50,30 C65,30 75,35 75,45 L75,110 C75,118 68,125 60,125 L40,125 C32,125 25,118 25,110 Z" stroke="black" stroke-width="5" fill="none" />
                                            <rect x="42" y="22" width="16" height="10" rx="2" fill="black" />
                                        </svg>
                                    </div>
                                    <div class="text-center text-sm mt-3">
                                        <span class="font-bold" :class="bouteille.etat == 'récupération' ? 'text-slate-800' : (bouteille.pourcentage_remplissage < 20 ? 'text-red-600' : 'text-green-600')">
                                            {{ bouteille.pourcentage_remplissage }}%
                                        </span>
                                        <p class="text-xs">{{ bouteille.poids_actuel }} / {{ bouteille.poids_max }} kg</p>
                                    </div>
                                </div>

                                <div>
                                    <div class="mt-3 text-sm text-gray-600 space-y-1">
                                        <p v-if="!bouteille.is_recycle && bouteille.emplacement"><i class="fas fa-map-marker-alt w-5"></i> Emplacement {{ bouteille.emplacement.name || '—' }}</p>
                                    </div>
                                    <div v-if="bouteille.is_recycle" class="mt-6 bg-green-600 text-white rounded p-4">
                                        <p class="text-lg font-bold"><i class="fas fa-recycle w-5"></i> Bouteille recyclée</p>
                                        <p class="text-sm font-light">Trackdéchets: {{ bouteille.num_track_dechets || '—' }}</p>
                                    </div>
                                    <div v-if="!bouteille.is_recycle" class="mt-4 flex flex-wrap gap-2">
                                        <button @click="openGaz(bouteille)" class="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded">
                                            <i class="fas fa-exchange-alt mr-1"></i> Gaz
                                        </button>
                                        <button @click="openEmplacement(bouteille)" class="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded">
                                            <i class="fas fa-warehouse mr-1"></i> Emplacement
                                        </button>
                                        <button @click="openMovements(bouteille)" class="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded">
                                            <i class="fas fa-history mr-1"></i> Historique
                                        </button>
                                        <button @click="openRecycle(bouteille)" class="text-xs bg-gray-100 hover:bg-gray-200 px-3 py-1 rounded">
                                            <i class="fas fa-recycle mr-1"></i> Recyclage
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </app-template>

        <!-- MODALES -->
        <modal name="bouteille-form" height="auto" width="640" :scrollable="true" :adaptive="true">
            <div class="overflow-y-auto max-h-[100vh]">

                <!-- En-tête -->
                <div class="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
                    <div class="flex items-center gap-3">
                        <div class="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center">
                            <i class="fas fa-bottle-water text-blue-600 text-sm"></i>
                        </div>
                        <div>
                            <h2 class="text-lg font-bold text-gray-900 leading-tight">
                                {{ form.id ? 'Modifier la bouteille' : 'Nouvelle bouteille' }}
                            </h2>
                            <p class="text-xs text-gray-400 mt-0.5">Champs obligatoires marqués <span class="text-red-400">*</span></p>
                        </div>
                    </div>
                    <button @click="$modal.hide('bouteille-form')" class="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-400 hover:text-gray-600 transition" type="button">
                        <i class="fas fa-times"></i>
                    </button>
                </div>

                <div class="px-6 py-5 space-y-6">

                    <!-- ① Identité de la bouteille -->
                    <section>
                        <p class="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-3">Identité</p>
                        <div class="grid grid-cols-2 gap-3">

                            <!-- Numéro -->
                            <div class="col-span-2 sm:col-span-1">
                                <label class="block text-sm font-medium text-gray-700 mb-1">Numéro <span class="text-red-400">*</span></label>
                                <input
                                    v-model="form.numero"
                                    placeholder="Ex: B-001"
                                    class="border border-gray-200 rounded-xl px-4 py-2.5 w-full focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition text-sm"
                                    id="inp-numero"
                                />
                            </div>

                            <!-- Type de gaz -->
                            <div class="col-span-2 sm:col-span-1">
                                <label class="block text-sm font-medium text-gray-700 mb-1">Type de fluide <span class="text-red-400">*</span></label>
                                <div class="relative">
                                    <i class="fas fa-search absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-300 text-xs"></i>
                                    <input
                                        v-model="search"
                                        @input="filterFluides"
                                        @focus="showDropdown = true"
                                        @blur="() => setTimeout(() => { showDropdown = false }, 200)"
                                        type="text"
                                        id="inp-type-gaz"
                                        placeholder="R32, R410A…"
                                        class="border border-gray-200 rounded-xl pl-9 pr-9 py-2.5 w-full focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition text-sm"
                                        :class="selectedFluide ? 'border-green-400 bg-green-50' : ''"
                                    />
                                    <!-- Indicateur fluide sélectionné -->
                                    <div v-if="selectedFluide" class="absolute right-3 top-1/2 -translate-y-1/2">
                                        <span class="text-[10px] font-bold px-1.5 py-0.5 bg-green-100 text-green-700 rounded-full">{{ selectedFluide.type }}</span>
                                    </div>
                                    <ul v-if="showDropdown && filteredFluides.length" class="absolute z-50 left-0 right-0 mt-1.5 bg-white rounded-2xl shadow-2xl border border-gray-100 max-h-52 overflow-y-auto py-1.5">
                                        <li
                                            v-for="fluide in filteredFluides"
                                            :key="fluide.code"
                                            @mousedown.prevent="selectFluide(fluide)"
                                            class="px-4 py-2.5 hover:bg-blue-50 cursor-pointer transition-colors flex items-center justify-between"
                                        >
                                            <div>
                                                <span class="font-bold text-gray-900 text-sm">{{ fluide.code }}</span>
                                                <span class="text-xs text-gray-400 ml-2">{{ fluide.nom }}</span>
                                            </div>
                                            <span class="text-[10px] font-semibold px-2 py-0.5 bg-gray-100 rounded-full text-gray-500">{{ fluide.type }}</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </section>

                    <!-- ② Type de bouteille -->
                    <section>
                        <p class="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-3">Type de bouteille <span class="text-red-400">*</span></p>
                        <div class="grid grid-cols-2 gap-3" id="inp-type">
                            <button
                                type="button"
                                @click="form.etat = 'charge'"
                                :class="form.etat === 'charge'
                                    ? 'border-2 border-blue-500 bg-blue-50 text-blue-700'
                                    : 'border-2 border-gray-200 bg-white text-gray-500 hover:border-gray-300'"
                                class="rounded-2xl p-4 text-left transition-all"
                            >
                                <div class="flex items-center gap-2 mb-1.5">
                                    <div :class="form.etat === 'charge' ? 'bg-blue-500' : 'bg-gray-200'" class="w-4 h-4 rounded-full flex items-center justify-center transition-colors flex-shrink-0">
                                        <i v-if="form.etat === 'charge'" class="fas fa-check text-white" style="font-size:8px"></i>
                                    </div>
                                    <span class="font-semibold text-sm">Bouteille de charge</span>
                                </div>
                                <p class="text-xs text-gray-400 ml-6">Contient du gaz neuf à injecter dans les installations</p>
                            </button>
                            <button
                                type="button"
                                @click="form.etat = 'récupération'"
                                :class="form.etat === 'récupération'
                                    ? 'border-2 border-amber-500 bg-amber-50 text-amber-700'
                                    : 'border-2 border-gray-200 bg-white text-gray-500 hover:border-gray-300'"
                                class="rounded-2xl p-4 text-left transition-all"
                            >
                                <div class="flex items-center gap-2 mb-1.5">
                                    <div :class="form.etat === 'récupération' ? 'bg-amber-500' : 'bg-gray-200'" class="w-4 h-4 rounded-full flex items-center justify-center transition-colors flex-shrink-0">
                                        <i v-if="form.etat === 'récupération'" class="fas fa-check text-white" style="font-size:8px"></i>
                                    </div>
                                    <span class="font-semibold text-sm">Récupération</span>
                                </div>
                                <p class="text-xs text-gray-400 ml-6">Collecte le gaz extrait des installations</p>
                            </button>
                        </div>
                    </section>

                    <!-- ③ Quantités -->
                    <section>
                        <p class="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-3">Quantités</p>
                        <div class="space-y-3">

                            <!-- Capacité -->
                            <div class="flex items-center gap-4 bg-gray-50 rounded-xl p-4 border border-gray-100">
                                <div class="flex-1">
                                    <label class="block text-sm font-medium text-gray-700 mb-0.5" for="inp-poids-max">
                                        <template v-if="form.etat === 'charge'">Quantité à l'achat</template>
                                        <template v-else>Capacité maximale</template>
                                        <span class="text-red-400 ml-1">*</span>
                                    </label>
                                    <p class="text-xs text-gray-400">Quantité de gaz lors de l'achat ou livraison</p>
                                </div>
                                <div class="flex items-center gap-2 flex-shrink-0">
                                    <input
                                        v-model.number="form.poids_max"
                                        type="number"
                                        min="0"
                                        placeholder="0"
                                        class="border border-gray-200 rounded-xl px-3 py-2 w-24 text-right focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition text-sm font-mono"
                                        id="inp-poids-max"
                                    />
                                    <span class="text-sm font-medium text-gray-400">kg</span>
                                </div>
                            </div>

                            <!-- Déjà utilisée -->
                            <div
                                class="flex items-center gap-3 rounded-xl px-4 py-3 border cursor-pointer transition-all"
                                :class="formDejaUtilisee ? 'border-orange-300 bg-orange-50' : 'border-gray-200 bg-white hover:bg-gray-50'"
                                @click="toggleDejaUtilisee"
                            >
                                <div
                                    class="w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 transition-all"
                                    :class="formDejaUtilisee ? 'border-orange-500 bg-orange-500' : 'border-gray-300 bg-white'"
                                >
                                    <i v-if="formDejaUtilisee" class="fas fa-check text-white" style="font-size:9px"></i>
                                </div>
                                <div class="flex-1">
                                    <p class="text-sm font-medium" :class="formDejaUtilisee ? 'text-orange-800' : 'text-gray-700'">
                                        Bouteille déjà utilisée / partiellement remplie
                                    </p>
                                    <p class="text-xs text-gray-400 mt-0.5">Préciser la quantité restante</p>
                                </div>
                            </div>

                            <!-- Poids actuel — affiché si déjà utilisée -->
                            <transition name="slide-down">
                                <div v-if="formDejaUtilisee" class="flex items-center gap-4 bg-orange-50 rounded-xl p-4 border border-orange-200">
                                    <div class="flex-1">
                                        <label class="block text-sm font-medium text-orange-800 mb-0.5" for="inp-poids-actuel">Quantité actuellement dans la bouteille</label>
                                        <p class="text-xs text-orange-400">
                                            <template v-if="form.poids_max">Maximum : {{ form.poids_max }} kg</template>
                                            <template v-else>Renseignez d'abord la capacité ci-dessus</template>
                                        </p>
                                    </div>
                                    <div class="flex items-center gap-2 flex-shrink-0">
                                        <input
                                            v-model.number="form.poids_actuel"
                                            type="number"
                                            min="0"
                                            :max="form.poids_max || undefined"
                                            placeholder="0"
                                            class="border border-orange-300 rounded-xl px-3 py-2 w-24 text-right focus:outline-none focus:border-orange-400 focus:ring-2 focus:ring-orange-100 transition text-sm font-mono bg-white"
                                            id="inp-poids-actuel"
                                        />
                                        <span class="text-sm font-medium text-orange-400">kg</span>
                                    </div>
                                </div>
                            </transition>
                        </div>
                    </section>

                    <!-- ④ Emplacement -->
                    <section>
                        <p class="text-[11px] font-semibold text-gray-400 uppercase tracking-widest mb-3">Emplacement de stockage</p>

                        <!-- Cartes cliquables si ≤ 5 emplacements -->
                        <div v-if="emplacements.length <= 5" class="flex flex-wrap gap-2">
                            <button
                                v-for="loc in emplacements"
                                :key="loc.id"
                                type="button"
                                @click="form.emplacement_id = loc.id"
                                :class="form.emplacement_id === loc.id
                                    ? 'border-2 border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
                                    : 'border-2 border-gray-200 bg-white text-gray-600 hover:border-gray-300'"
                                class="flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all text-sm font-medium"
                            >
                                <i class="fas fa-warehouse text-xs" :class="form.emplacement_id === loc.id ? 'text-blue-500' : 'text-gray-300'"></i>
                                {{ loc.name }}
                                <i v-if="form.emplacement_id === loc.id" class="fas fa-check text-blue-500 text-xs ml-1"></i>
                            </button>
                            <button
                                type="button"
                                @click="form.emplacement_id = null"
                                :class="form.emplacement_id === null
                                    ? 'border-2 border-gray-400 bg-gray-100 text-gray-600'
                                    : 'border-2 border-dashed border-gray-200 text-gray-400 hover:border-gray-300'"
                                class="flex items-center gap-2 px-4 py-2.5 rounded-xl transition-all text-sm"
                            >
                                <i class="fas fa-times text-xs"></i>
                                Aucun
                            </button>
                        </div>

                        <!-- Select si > 5 emplacements -->
                        <select
                            v-else
                            v-model="form.emplacement_id"
                            class="border border-gray-200 rounded-xl px-4 py-2.5 w-full focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-50 transition text-sm"
                        >
                            <option :value="null">— Aucun emplacement —</option>
                            <option v-for="loc in emplacements" :key="loc.id" :value="loc.id">{{ loc.name }}</option>
                        </select>
                    </section>

                    <!-- ⑤ Adresse de recyclage -->
                    <section>
                        <div class="flex items-center gap-2 mb-3">
                            <p class="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">Adresse de recyclage (TrackDéchets)</p>
                            <span class="text-red-400 text-sm font-bold">*</span>
                        </div>

                        <div class="text-sm bg-yellow-50 border border-yellow-200 text-yellow-800 p-3 rounded-xl mb-3" v-if="form?.id && form.poids_max > 0 && form.etat === 'charge'">
                            <i class="fas fa-exclamation-triangle mr-1.5"></i> Adresse utilisée sur des CERFA existants — ils ne seront pas mis à jour automatiquement.
                        </div>

                        <!-- Adresses existantes -->
                        <div v-if="adressesList && adressesList.length" class="space-y-2 mb-3">
                            <button
                                v-for="(adresse, i) in adressesList"
                                :key="i"
                                v-if="adresse"
                                type="button"
                                @click="selectAdresseExistante(adresse)"
                                :class="form.recuperation_adresse_id === adresse.id
                                    ? 'border-2 border-blue-500 bg-blue-50'
                                    : 'border-2 border-gray-200 bg-white hover:border-gray-300'"
                                class="w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all text-left"
                            >
                                <div class="flex items-center gap-3">
                                    <div :class="form.recuperation_adresse_id === adresse.id ? 'bg-blue-500' : 'bg-gray-200'" class="w-4 h-4 rounded-full flex items-center justify-center transition-colors flex-shrink-0">
                                        <i v-if="form.recuperation_adresse_id === adresse.id" class="fas fa-check text-white" style="font-size:8px"></i>
                                    </div>
                                    <span class="text-sm text-gray-700">{{ adresse.adresse1 }}, {{ adresse.code_postal }} {{ adresse.ville }}</span>
                                </div>
                            </button>

                            <button
                                type="button"
                                @click="selectNouvelleAdresse"
                                :class="displayNewAdresse ? 'border-2 border-blue-500 bg-blue-50' : 'border-2 border-dashed border-gray-300 bg-white hover:border-gray-400'"
                                class="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-left"
                            >
                                <div :class="displayNewAdresse ? 'bg-blue-500' : 'bg-gray-200'" class="w-4 h-4 rounded-full flex items-center justify-center transition-colors flex-shrink-0">
                                    <i v-if="displayNewAdresse" class="fas fa-check text-white" style="font-size:8px"></i>
                                    <i v-else class="fas fa-plus text-white" style="font-size:8px"></i>
                                </div>
                                <span class="text-sm text-gray-600 font-medium">Nouvelle adresse…</span>
                            </button>
                        </div>

                        <!-- InputAdresse -->
                        <transition name="slide-down">
                            <div class="bg-gray-50 p-4 rounded-xl border border-gray-200" v-if="displayNewAdresse || !adressesList.length">
                                <input-adresse
                                    v-model="formAdresseRecup"
                                    :show-recap="true"
                                    placeholder="12 rue des Fleurs, Lyon…"
                                    @address-selected="onAdresseRecupSelected"
                                />
                            </div>
                        </transition>
                    </section>
                </div>

                <!-- Footer actions -->
                <div class="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 rounded-b-lg">
                    <button
                        @click="$modal.hide('bouteille-form')"
                        class="px-5 py-2.5 rounded-xl text-sm font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 transition"
                        type="button"
                    >
                        Annuler
                    </button>
                    <button
                        @click="submitForm"
                        class="px-5 py-2.5 rounded-xl text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 transition shadow-sm disabled:opacity-60"
                        :disabled="loadingEditAddBouteille"
                        type="button"
                    >
                        <i v-if="loadingEditAddBouteille" class="fas fa-spinner fa-spin mr-2"></i>
                        <i v-else class="fas fa-save mr-2"></i>
                        {{ form.id ? 'Enregistrer' : 'Créer la bouteille' }}
                    </button>
                </div>
            </div>
        </modal>

        <modal name="gaz-form" height="auto" width="400">
            <div class="p-6 space-y-4">
                <h2 class="text-lg font-bold">Mouvement de gaz</h2>
                <template v-if="loadingAddGaz">
                    <div class="flex justify-center items-center py-8"><i class="fas fa-spinner fa-spin text-3xl"></i></div>
                </template>
                <template v-if="!loadingAddGaz">
                    <div v-if="current">
                        <label for="inp-qtt-gaz">Quantité à {{ current.etat == 'charge' ? 'retirer' : 'ajouter' }} dans la bouteille (kg)</label>
                        <input v-model.number="gaz.quantite" type="number" placeholder="kg" class="border rounded px-3 py-2 w-full mt-1 focus:outline-none" id="inp-qtt-gaz" />
                    </div>
                    <div class="flex justify-end gap-2">
                        <button @click="$modal.hide('gaz-form')" class="bg-gray-200 px-4 py-2 rounded">Annuler</button>
                        <button @click="submitGaz" class="bg-blue-600 text-white px-4 py-2 rounded">Valider</button>
                    </div>
                </template>
            </div>
        </modal>

        <modal name="emplacement-form" height="auto" width="400">
            <div class="p-6 space-y-4">
                <h2 class="text-lg font-bold">Changer d'emplacement</h2>
                <select v-model="emplacement.emplacement_id" class="border rounded px-3 py-2 w-full focus:outline-none">
                    <option v-for="loc in emplacements" :key="loc.id" :value="loc.id">{{ loc.name }}</option>
                </select>
                <div class="flex justify-end gap-2">
                    <template v-if="loadingChangeEmplacement"><i class="fas fa-spinner fa-spin"></i></template>
                    <template v-if="!loadingChangeEmplacement">
                        <button @click="$modal.hide('emplacement-form')" class="bg-gray-200 px-4 py-2 rounded">Annuler</button>
                        <button @click="submitEmplacement" class="bg-blue-600 text-white px-4 py-2 rounded">Valider</button>
                    </template>
                </div>
            </div>
        </modal>

        <modal name="movements" height="auto" width="600">
            <div class="p-6">
                <div v-if="loadingGetMovements"><i class="fas fa-spinner fa-spin"></i> Chargement...</div>
                <div v-if="!loadingGetMovements">
                    <h2 class="text-lg font-bold mb-2">Historique des mouvements</h2>
                    <button @click="exportMovementsCsv" class="bg-slate-200 hover:bg-slate-300 duration-200 px-4 py-2 rounded text-xs font-light mb-4"><i class="fas fa-file-export mr-1"></i> Exporter</button>
                    <table class="w-full text-sm">
                        <thead>
                            <tr class="border bg-gray-100">
                                <th class="text-left py-2 px-2 border">Action</th>
                                <th class="text-left py-2 px-2 border">Détails</th>
                                <th class="text-left py-2 px-2 border">Utilisateur</th>
                                <th class="text-left py-2 px-2 border">Date</th>
                                <th class="text-left py-2 px-2 border">CERFA</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="m in movements" :key="m.id" class="border">
                                <td class="py-2 px-2 border font-semibold">{{ m.action }}</td>
                                <td class="py-2 px-2 border">{{ m.details }}</td>
                                <td class="py-2 px-2 border">
                                    <template v-if="m.account">{{ m.account.prenom + ' ' + m.account.nom }}</template>
                                    <template v-else>-</template>
                                </td>
                                <td class="py-2 px-2 border">{{ new Date(m.created_at).toLocaleString() }}</td>
                                <td class="py-2 px-2 border">
                                    <template v-if="m.cerfa">
                                        <a class="text-xs text-blue-600 hover:underline" target="_blank" :href="m.cerfa.document_url">Consulter</a>
                                    </template>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </modal>

        <modal name="recycle-form" height="auto" width="400">
            <div class="p-6 space-y-4">
                <h2 class="text-lg font-bold">Cette bouteille est recyclée</h2>
                <template v-if="current && current.etat == 'charge' && current.poids_actuel != 0">
                    <div class="bg-yellow-100 text-yellow-800 p-4 rounded">
                        <i class="fas fa-exclamation-triangle mr-2"></i>
                        La bouteille de charge n'est pas vide<br />
                        <span class="text-xs font-light">{{ current.poids_actuel }} kg restants</span>
                    </div>
                </template>
                <template v-if="current && current.etat == 'récupération' && current.poids_actuel != current.poids_total">
                    <div class="bg-yellow-100 text-yellow-800 p-4 rounded">
                        <i class="fas fa-exclamation-triangle mr-2"></i>
                        La bouteille n'est pas complétement pleine.
                    </div>
                </template>
                <template v-if="loadingTrackDechets">
                    <div class="flex justify-center items-center py-8"><i class="fas fa-spinner fa-spin text-3xl"></i></div>
                </template>
                <template v-if="!loadingTrackDechets">
                    <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Numéro de suivi des déchets (TrackDéchets)</label>
                        <input v-model="numTrackDechets" class="border border-gray-300 rounded-lg px-4 py-2.5 w-full focus:outline-none transition" id="inp-track-dechets" />
                    </div>
                    <div class="flex justify-end gap-2">
                        <button @click="$modal.hide('recycle-form')" class="bg-gray-200 px-4 py-2 rounded">Annuler</button>
                        <button @click="submitRecycle" class="bg-blue-600 text-white px-4 py-2 rounded">Valider</button>
                    </div>
                </template>
            </div>
        </modal>

        <!-- Export CSV -->
        <modal name="export-csv-modal" height="auto" :adaptive="true">
            <div class="p-6">
                <h2 class="text-lg font-bold mb-4">Export des bouteilles (CSV)</h2>
                <div class="space-y-4">
                    <div>
                        <label class="block text-sm font-medium mb-1">Type d'export</label>
                        <select v-model="exportFilters.type_export" class="w-full border rounded px-3 py-2">
                            <option value="mouvements">Mouvements des fluides</option>
                            <option value="bouteilles">Bouteilles</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-medium mb-1">Numéro de bouteille</label>
                        <input v-model="exportFilters.numero" type="text" placeholder="Ex : BT-001" class="w-full border rounded px-3 py-2">
                    </div>
                    <div>
                        <label class="block text-sm font-medium mb-1">Type de gaz</label>
                        <div class="relative group">
                            <i class="fas fa-search absolute left-4 top-1/2 -translate-y-1/2 text-gray-300"></i>
                            <input
                                v-model="search"
                                @input="filterFluides"
                                @focus="showDropdown = true"
                                type="text"
                                placeholder="Rechercher un fluide (ex: R32...)"
                                class="border border-gray-300 pl-10 pr-4 py-2.5 rounded-lg w-full focus:outline-none transition"
                            />
                            <ul v-if="showDropdown && filteredFluides.length" class="absolute z-50 left-0 right-0 mt-2 bg-white rounded-2xl shadow-2xl border border-gray-100 max-h-60 overflow-y-auto py-2">
                                <li v-for="fluide in filteredFluides" :key="fluide.code" @mousedown.prevent="selectFluide(fluide)" class="px-5 py-3 hover:bg-gray-50 cursor-pointer transition-colors">
                                    <div class="flex justify-between items-center">
                                        <span class="font-black text-gray-900">{{ fluide.code }}</span>
                                        <span class="text-[10px] font-bold px-2 py-0.5 bg-gray-100 rounded-full text-gray-600">{{ fluide.type }}</span>
                                    </div>
                                    <div class="text-xs text-gray-400 mt-1">{{ fluide.nom }}</div>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <div>
                        <label class="block text-sm font-medium mb-1">Type de bouteille</label>
                        <select v-model="exportFilters.etat" class="w-full border rounded px-3 py-2">
                            <option :value="null">Tous</option>
                            <option v-for="etat in etatList" :key="etat" :value="etat">{{ etat }}</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-medium mb-1">Emplacement</label>
                        <select v-model="exportFilters.emplacement_id" class="w-full border rounded px-3 py-2">
                            <option :value="null">Tous</option>
                            <option v-for="emp in emplacements" :key="emp.id" :value="emp.id">{{ emp.name }}</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-medium mb-1">Recyclage</label>
                        <select v-model="exportFilters.is_recycle" class="w-full border rounded px-3 py-2">
                            <option :value="null">Toutes</option>
                            <option :value="1">Recyclées</option>
                            <option :value="0">Non recyclées</option>
                        </select>
                    </div>
                    <div>
                        <label class="block text-sm font-medium mb-1">Date début</label>
                        <input v-model="exportFilters.date_debut" type="date" class="w-full border rounded px-3 py-2">
                    </div>
                    <div>
                        <label class="block text-sm font-medium mb-1">Date fin</label>
                        <input v-model="exportFilters.date_fin" type="date" class="w-full border rounded px-3 py-2">
                    </div>
                </div>
                <div class="flex justify-end mt-6 space-x-3">
                    <button class="px-4 py-2 rounded border" @click="$modal.hide('export-csv-modal')">Annuler</button>
                    <button class="bg-green-600 text-white px-4 py-2 rounded flex items-center" @click="exportCsv" :disabled="loadingExport">
                        <i v-if="loadingExport" class="fas fa-spinner fa-spin mr-2"></i>
                        Exporter
                    </button>
                </div>
            </div>
        </modal>
    </div>
</template>

<script>
import axios from 'axios'
import env from '@/services/env.service'

import InputAdresse from '@/components/InputAdresse.vue'   // ← remplace AdresseForm
import fluidesList from '@/config/fluides'

export default {
    components: {
        InputAdresse,   // ← remplace AdresseForm
    },

    watch: {
        filters: {
            deep: true,
            handler() {
                clearTimeout(this._searchTimer)
                this._searchTimer = setTimeout(() => { this.load() }, 300)
            }
        }
    },

    data() {
        return {
            fluides: fluidesList,
            selectedFluide: null,
            showErrors: false,
            search: '',
            showDropdown: false,
            filteredFluides: [],

            bouteilles: [],
            current: null,
            form: {},
            gaz: {},
            emplacement: {},
            movements: [],
            emplacements: [],

            formAdresseRecup: {},    // alimenté par <input-adresse v-model>
            formDejaUtilisee: false, // toggle "bouteille déjà utilisée / partielle"

            numTrackDechets: '',

            searchFilter: '',
            showDropdownFilters: false,
            filteredFluidesFilter: [],
            filters: {
                numero: null,
                type_gaz: null,
                etat: null,
                emplacement_id: null,
                is_recycle: 1,
                date_debut: null,
                date_fin: null,
            },

            exportFilters: {
                type_export: 'mouvements',
                numero: null,
                type_gaz: null,
                etat: null,
                emplacement_id: null,
                is_recycle: null,
                date_debut: null,
                date_fin: null,
            },

            etatList: ['charge', 'récupération'],

            adressesList: [],
            displayNewAdresse: false,

            loadingList: false,
            loadingChangeEmplacement: false,
            loadingEditAddBouteille: false,
            loadingGetMovements: false,
            loadingAddGaz: false,
            loadingTrackDechets: false,
            loadingExport: false,
        }
    },

    methods: {
        async load() {
            this.loadingList = true
            this.bouteilles = await this.$request.get('/bouteilles', { params: { ...this.filters } })
            this.loadingList = false
        },

        openCreate() {
            this.form = {
                numero: '',
                type_gaz: '',
                poids_max: null,
                poids_actuel: null,
                recuperation_adresse_id: null,
                emplacement_id: null,
                etat: 'charge',
            }
            this.formAdresseRecup = {}
            this.formDejaUtilisee = false
            this.search = ''
            this.selectedFluide = null
            this.displayNewAdresse = !this.adressesList.length

            if (this.emplacements.length === 1) this.form.emplacement_id = this.emplacements[0].id
            if (this.adressesList.length === 1)  this.form.recuperation_adresse_id = this.adressesList[0].id

            this.$modal.show('bouteille-form')
        },

        openEdit(b) {
            this.form = { ...b }
            this.formAdresseRecup = b.adresse_recuperation ? { ...b.adresse_recuperation } : {}
            this.selectedFluide = this.fluides.find(f => f.code === b.type_gaz) || null
            this.displayNewAdresse = false
            this.search = this.selectedFluide ? this.selectedFluide.code : ''
            // Si poids_actuel différent de poids_max → déjà utilisée
            this.formDejaUtilisee = b.poids_actuel != null && b.poids_actuel !== b.poids_max
            this.$modal.show('bouteille-form')
        },

        toggleDejaUtilisee() {
            this.formDejaUtilisee = !this.formDejaUtilisee
            if (!this.formDejaUtilisee) this.form.poids_actuel = null
        },

        selectAdresseExistante(adresse) {
            this.form.recuperation_adresse_id = adresse.id
            this.displayNewAdresse = false
            this.formAdresseRecup = {}
        },

        selectNouvelleAdresse() {
            this.displayNewAdresse = true
            this.form.recuperation_adresse_id = null
            this.formAdresseRecup = {}
        },

        async submitForm() {
            // Validation champs principaux
            const validation = this.$inpError([
                'inp-numero:required|min=2',
                'inp-type-gaz:required|min=2',
                'inp-poids-max:required|number|min=1',
            ])
            if (!validation) {
                this.$toast.error('Formulaire invalide')
                return
            }
            if (!this.form.etat) {
                this.$toast.error('Veuillez choisir un type de bouteille')
                return
            }

            // Validation adresse de recyclage
            const needsNewAdresse = this.displayNewAdresse || !this.adressesList.length
            if (!this.form.recuperation_adresse_id) {
                if (!needsNewAdresse) {
                    this.$toast.error('Veuillez sélectionner ou ajouter une adresse de recyclage')
                    return
                }
                // Valider directement sur formAdresseRecup (InputAdresse gère la saisie)
                if (!this.formAdresseRecup.adresse1 || this.formAdresseRecup.adresse1.length < 5) {
                    this.$toast.error('Veuillez saisir une adresse valide')
                    return
                }
                if (!this.formAdresseRecup.code_postal) {
                    this.$toast.error('Veuillez saisir un code postal')
                    return
                }
                if (!this.formAdresseRecup.ville) {
                    this.$toast.error('Veuillez saisir une ville')
                    return
                }
            }

            if (this.form.poids_max < 0) {
                this.$toast.error('La quantité de gaz ne peut pas être négative')
                return
            }
            if (this.form.poids_actuel) {
                if (this.form.poids_actuel < 0) {
                    this.$toast.error('La quantité actuelle de gaz ne peut pas être négative')
                    return
                }
                if (this.form.poids_actuel > this.form.poids_max) {
                    this.$toast.error('La quantité actuelle de gaz ne peut pas dépasser la capacité maximale de la bouteille')
                    return
                }
            }

            this.loadingEditAddBouteille = true

            // Créer l'adresse si nouvelle saisie via InputAdresse
            if (!this.form.recuperation_adresse_id) {
                const responseAdresse = await this.$request.post('/clients/0/adresses', {
                    ...this.formAdresseRecup,
                    no_client: 1,
                })
                this.form.recuperation_adresse_id = responseAdresse.id
            }

            const url = this.form.id ? `/bouteilles/${this.form.id}/update` : '/bouteilles'
            await this.$request.post(url, this.form)

            this.loadingEditAddBouteille = false
            this.$modal.hide('bouteille-form')
            this.load()
            this.$eventHub.$emit('refresh-warnings')
            this.getAdresses()
        },

        // Appelé quand l'utilisateur sélectionne une adresse dans InputAdresse
        // On reset l'id existant pour que le submitForm crée bien une nouvelle adresse
        onAdresseRecupSelected() {
            this.form.recuperation_adresse_id = null
        },

        openGaz(b) {
            this.current = b
            this.gaz = { quantite: null }
            this.$modal.show('gaz-form')
        },

        async submitGaz() {
            if (!this.$inpError(['inp-qtt-gaz:required'])) {
                this.$toast.error('Formulaire invalide')
                return
            }
            if (this.current.etat == 'charge' && this.current.poids_actuel - this.gaz.quantite < 0) {
                this.$toast.error('La quantité dépasse le poids maximum de la bouteille')
                return
            }
            if (this.current.etat == 'récupération' && this.gaz.quantite + this.current.poids_actuel > this.current.poids_max) {
                this.$toast.error('La bouteille ne peut pas contenir autant de gaz')
                return
            }
            this.loadingAddGaz = true
            if (this.current.etat == 'charge') this.gaz.quantite = -this.gaz.quantite
            await this.$request.post(`/bouteilles/${this.current.id}/gaz`, this.gaz)
            this.$modal.hide('gaz-form')
            await this.load()
            this.loadingAddGaz = false
        },

        openEmplacement(b) {
            this.current = b
            this.emplacement = { emplacement_id: b.emplacement_id }
            this.$modal.show('emplacement-form')
        },

        async submitEmplacement() {
            this.loadingChangeEmplacement = true
            await this.$request.post(`/bouteilles/${this.current.id}/emplacement`, this.emplacement)
            await this.load()
            this.$modal.hide('emplacement-form')
            this.loadingChangeEmplacement = false
        },

        async openMovements(b) {
            this.$modal.show('movements')
            this.loadingGetMovements = true
            this.movements = await this.$request.get(`/bouteilles/${b.id}/movements`)
            this.current = b
            this.loadingGetMovements = false
        },

        async getEmplacements() {
            const response = await this.$request.get('/stock/locations')
            this.emplacements = response.locations || []
        },

        openRecycle(b) {
            this.current = b
            this.$modal.show('recycle-form')
        },

        async submitRecycle() {
            if (!this.$inpError(['inp-track-dechets:required|min=2'])) {
                this.$toast.error('Formulaire invalide')
                return
            }
            this.loadingTrackDechets = true
            await this.$request.post(`/bouteilles/${this.current.id}/recycle`, { num_track_dechets: this.numTrackDechets })
            this.$modal.hide('recycle-form')
            await this.load()
            this.loadingTrackDechets = false
        },

        filterFluides() {
            const s = this.search.toLowerCase().trim()
            // Si l'utilisateur modifie le texte après sélection, on reset
            if (this.selectedFluide && this.search !== this.selectedFluide.code) {
                this.selectedFluide = null
                this.form.type_gaz = ''
            }
            if (!s) { this.filteredFluides = []; return }
            this.filteredFluides = this.fluides.filter(f => f.code.toLowerCase().includes(s) || f.nom.toLowerCase().includes(s)).slice(0, 10)
        },

        filterFluidesFilter() {
            const s = this.searchFilter.toLowerCase().trim()
            if (!s) { this.filteredFluidesFilter = []; this.filters.type_gaz = null; return }
            this.filteredFluidesFilter = this.fluides.filter(f => f.code.toLowerCase().includes(s) || f.nom.toLowerCase().includes(s)).slice(0, 10)
        },

        selectFluide(f) {
            this.selectedFluide = f
            this.form.type_gaz = f.code
            this.search = f.code          // affiche le code dans l'input
            this.filteredFluides = []     // vide les suggestions
            this.showDropdown = false
        },

        selectFluideFilter(f) {
            this.filters.type_gaz = f.code
            this.searchFilter = f.code
            this.showDropdownFilters = false
        },

        exportMovementsCsv() {
            const headers = ['Action', 'Détails', 'Utilisateur', 'Date', 'CERFA']
            const rows = this.movements.map(m => [
                m.action, m.details,
                m.account ? `${m.account.prenom} ${m.account.nom}` : '-',
                new Date(m.created_at).toLocaleString(),
                m.cerfa ? m.cerfa.document_url : '-',
            ])
            let csv = 'data:text/csv;charset=utf-8,' + headers.join(',') + '\n'
            rows.forEach(r => { csv += r.map(f => `"${f}"`).join(',') + '\n' })
            const link = document.createElement('a')
            link.setAttribute('href', encodeURI(csv))
            link.setAttribute('download', `bouteille_${this.current.numero}_movements.csv`)
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
        },

        openExportModal() {
            const d = new Date()
            d.setFullYear(d.getFullYear() - 1)
            this.exportFilters = {
                type_export: 'mouvements',
                date_debut: d.toISOString().split('T')[0],
                date_fin: new Date().toISOString().split('T')[0],
                is_recycle: null, emplacement_id: null, etat: null,
            }
            this.$modal.show('export-csv-modal')
        },

        async exportCsv() {
            this.loadingExport = true
            try {
                const response = await axios.post(
                    env.apiUrl + '/bouteilles/export/csv',
                    { ...this.exportFilters },
                    { responseType: 'blob', headers: { 'Authorization': 'Bearer ' + env.authToken } }
                )
                const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' })
                const url = window.URL.createObjectURL(blob)
                let fileName = 'export.csv'
                const cd = response.headers['content-disposition']
                if (cd) { const m = cd.match(/filename="?([^"]+)"?/); if (m) fileName = m[1] }
                const link = document.createElement('a')
                link.href = url
                link.download = fileName
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                window.URL.revokeObjectURL(url)
                this.$toast.success('Export CSV généré avec succès')
                this.$modal.hide('export-csv-modal')
            } catch (e) {
                console.error(e)
                this.$toast.error('Erreur lors de la génération du CSV')
            }
            this.loadingExport = false
        },

        async getAdresses() {
            const response = await this.$request.post('/bouteilles/adresses-recyclage')
            this.adressesList = response.adresses
        },
    },

    mounted() {
        this.load()
        this.getEmplacements()
        this.getAdresses()
    },
}
</script>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
    transition: max-height 0.25s ease, opacity 0.2s ease;
    overflow: hidden;
    max-height: 400px;
}
.slide-down-enter,
.slide-down-leave-to {
    max-height: 0;
    opacity: 0;
}
</style>